import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "react-query";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Container,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import type { AxiosError } from "axios";
import styled from "@emotion/styled";
import { useRouter } from "next/router";

import MainLayout from "@/layouts/MainLayout";
import auth from "@/services/auth";
import basePaths from "@/services/basePaths";
import api from "@/services/api";
import intercom from "@/services/intercom";
import { useIsMedium } from "@/styles/breakpoints";
import { IsBusinessContext } from "../../contexts";
import { conversion, conversions } from "@/services/ga";
import Links from "@/constants/Links";

const selfServeApplicationSchema = (isBusiness: boolean) =>
  yup.object().shape({
    email: yup.string().email().required().label("Email"),
    firstName: yup.string().required().label("First Name"),
    lastName: yup.string().required().label("Last Name"),
    ...(isBusiness
      ? {}
      : { agencyName: yup.string().required().label("Agency Name") }),
  });

const postCreateUser = ({ agencyName, email, firstName, lastName }) =>
  axios.post(`${basePaths.api}/v1/user/`, {
    agency_name: agencyName,
    email,
    first_name: firstName,
    last_name: lastName,
  });

const postSendAccessEmailToExistingUser = ({ email }: { email: string }) =>
  api.post("/v1/getcyber/user/send-access-email-to-existing-user", { email });

const Title = styled(Typography)`
  font-weight: 600;
  font-size: 28px;
  color: #000000;
`;

const BackgroundImage = styled.img`
  height: 600px;
  bottom: 0;
  position: absolute;
  z-index: -1;
  display: none;

  @media (min-width: 960px) and (min-height: 1080px) {
    display: block;
  }
`;

export default function Quote1() {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const isMedium = useIsMedium();
  const [showAccessEmailSentToExistingUserModal, setShowAccessEmailSentToExistingUserModal] = useState(false);
  const { isBusiness } = useContext(IsBusinessContext);

  useEffect(() => {
    if (isBusiness === null) {
      // If we don't know whether they're a business or not, we need them to select
      // which client portal they want via the homepage.
      router.push(Links.INDEX);
    }
  }, [isBusiness]);

  const form = useForm({
    resolver: yupResolver(selfServeApplicationSchema(isBusiness)),
    defaultValues: {
      agencyName: "",
      email: "",
      firstName: "",
      lastName: "",
    },
  });

  const {
    mutateAsync: mutateSendAccessEmailToExistingUser,
    status: sendAccessEmailToExistingUserStatus,
  } = useMutation(
    "v1/getcyber/user/send-access-email-to-existing-user",
    postSendAccessEmailToExistingUser,
    {
      onSuccess: () => {
        enqueueSnackbar(`Email sent.`, {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(`Unable to send email.`, {
          variant: "error",
        });
      },
    }
  );

  const { mutateAsync: mutateCreateUser, status: mutateCreateUserStatus } =
    useMutation<
      {
        data: {
          id: number;
          email: string;
          first_name: string;
          last_name: string;
          auth_token: string;
        };
      },
      AxiosError,
      {
        agencyName?: string;
        email: string;
        firstName: string;
        lastName: string;
      }
    >(postCreateUser, {
      onSuccess: (response) => {
        auth.storeToken(response.data.auth_token);
        intercom.update({
          email: response.data.email,
          name: `${response.data.first_name} ${response.data.last_name}`,
          user_id: response.data.id,
        });
      },
      onError: (err) => {
        if (err?.response?.data?.email) {
          mutateSendAccessEmailToExistingUser({ email: form.getValues("email") });
          setShowAccessEmailSentToExistingUserModal(true);
        } else {
          const errMsg = err?.response?.data
            ? Object.values(err.response.data).join(", ")
            : null;
          enqueueSnackbar(`There was an error${errMsg ? `: ${errMsg}` : "."}`, {
            variant: "error",
          });
        }
      },
    });

  const onSubmit = ({ email, agencyName, firstName, lastName }) => {
    conversion(conversions.submit_user_info);
    mutateCreateUser({ email, agencyName, firstName, lastName });
  };

  useEffect(() => {
    if (auth.isAuthenticated()) {
      router.push(Links.QUOTE_APPLICATION_SETUP);
    }
  }, [auth.isAuthenticated()]);

  return (
    <MainLayout
      backgroundImage={
        <BackgroundImage src="/images/get_started_background_waves.png" />
      }
      ctaOutlined
    >
      <Container>
        <Grid container spacing={10} alignItems="center">
          <Grid item sm={12} md={6}>
            <Box mb={4}>
              <Title variant="h3">Get Started</Title>
            </Box>

            <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
              <Box mb={4}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First Name"
                      name="firstName"
                      error={!!form.errors?.firstName}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputRef={form.register}
                      placeholder="John"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Last Name"
                      name="lastName"
                      error={!!form.errors?.lastName}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputRef={form.register}
                      placeholder="Doe"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box mb={4}>
                <TextField
                  label="Email"
                  name="email"
                  error={!!form.errors?.email}
                  helperText={
                    form.errors?.email?.message ||
                    "This is where you will receive your quotes."
                  }
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputRef={form.register}
                  placeholder={
                    isBusiness
                      ? "john.doe@mycompany.com"
                      : "john.doe@insuranceagency.com"
                  }
                />
              </Box>

              {!isBusiness && (
                <Box mb={4}>
                  <TextField
                    label="Agency Name"
                    name="agencyName"
                    error={!!form.errors?.agencyName}
                    helperText={form.errors?.agencyName?.message}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputRef={form.register}
                    placeholder="Insurance Brokerage LLC DBA Pacific Broker Partners"
                  />
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                endIcon={
                  mutateCreateUserStatus === "loading" && (
                    <CircularProgress size="small" />
                  )
                }
                disabled={mutateCreateUserStatus === "loading"}
              >
                Next
              </Button>
            </form>
          </Grid>

          {isMedium && (
            <Grid item md={6}>
              <img
                alt="Man sitting in a chair"
                src="/images/man_in_chair.png"
                style={{
                  width: "100%",
                }}
              />
            </Grid>
          )}
        </Grid>

        <Dialog
          onClose={() => {
            setShowAccessEmailSentToExistingUserModal(false);
          }}
          open={!!showAccessEmailSentToExistingUserModal}
          maxWidth="sm"
          disableEnforceFocus
        >
          <DialogContent>
            <Box mb={3} mt={2} textAlign="center">
              <Typography variant="h3">Check your email to log in</Typography>
            </Box>
            <Box textAlign="center" mb={3}>
              <Typography variant="body1">
                We sent you an email with a link to sign in.
              </Typography>
            </Box>
            <Box textAlign="center" mb={3}>
              <Typography variant="body1">
                Don’t see an email?{" "}
                <Button
                  variant="text"
                  color="primary"
                  onClick={() =>
                    mutateSendAccessEmailToExistingUser({ email: form.getValues("email") })
                  }
                  disabled={
                    sendAccessEmailToExistingUserStatus === "loading" ||
                    sendAccessEmailToExistingUserStatus === "success"
                  }
                  endIcon={
                    sendAccessEmailToExistingUserStatus === "loading" && (
                      <CircularProgress size={12} />
                    )
                  }
                >
                  Click here to resend
                </Button>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Box px={2} pb={3}>
              <Typography variant="caption" color="textSecondary">
                Why am I getting this?
              </Typography>
              <Typography variant="body2">
                It looks like you are accessing GetCyber.com from a new computer
                or browser. As a security measure to protect your account, we
                need to confirm who you are.
              </Typography>
            </Box>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
}
